import React from "react";
import PropTypes from "prop-types";
import { CheckCircle2, ArrowRight } from "lucide-react";
import ResponsiveButton from "../ResponsiveButton/NeonButton.jsx"; // tämä on sama NeonButton

/* --- pillien tyylit --- */
const pillBase = [
  "group relative inline-flex items-center gap-1.5 sm:gap-2",
  "rounded-full px-2 py-0.5 sm:px-3 sm:py-1.5",
  "text-[11px] text-xs md:text-lg",
  "font-normal text-shadow-sm tracking-wide",
  "transition duration-300 ease-out",
  "focus:outline-none focus:ring-2 focus:ring-pink/60",
  "after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:opacity-0",
  // shimmer-animaatio vain md+ ruuduille
  "after:bg-shimmer after:bg-[length:700px_100%] md:group-hover:after:opacity-40 md:group-hover:after:animate-shimmer",
].join(" ");


const pillSelected =
  "text-black/80 bg-pink ring-1 ring-pink shadow-neon-pink before:opacity-60 before:blur-[8px]";

/* --- yksittäinen bullet-rivi --- */
function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="mt-[2px] h-4 w-4 flex-none text-primary drop-shadow-[0_0_6px_rgba(49,255,217,0.6)]" />
      {/* väri tulee nyt ul:sta → ei omaa text-* -luokkaa */}
      <span className="font-customFont1 leading-relaxed">
        {children}
      </span>
    </li>
  );
}
Bullet.propTypes = { children: PropTypes.node };

/* --- pääsektion komponentti --- */
export default function WhyBuukkeriSection({
  className = "",
  // ✅ vaihdetaan oletusreitiksi esiintyjän liittymissivu
  learnMoreHref = "/rekisteroidy",
}) {
  // Pikahaun CTA – avaa varaus/haun (sama event kuin Bookkausneuvo.jsx)
  const openBooking = () =>
    window.dispatchEvent(new CustomEvent("bilebox:openBooking"));

  return (
    <section
      id="why-buukkeri"
      className={`relative py-12 sm:py-16 md:py-20 ${className}`}
    >
      <div className="container mx-auto">
        <div className="group relative overflow-hidden rounded-2xl bg-white/70 dark:bg-night-800/80 md:backdrop-blur-md border border-white/10 dark:border-white/5 shadow-lg md:hover:shadow-neon md:transition-shadow md:duration-500">

          <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/5 dark:ring-white/10 md:transition-shadow md:duration-500 md:group-hover:shadow-[0_0_24px_rgba(49,255,217,0.35),0_0_48px_rgba(122,0,255,0.25)]" />

          <span className="pointer-events-none absolute -left-1/3 top-0 h-full w-1/2 bg-gradient-to-r from-transparent via-white/15 to-transparent md:translate-x-[-120%] md:group-hover:translate-x-[180%] md:transition-transform md:duration-700 ease-out rounded-2xl" />


          <div className="relative sm:p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {/* Vasen paneeli: asiakkaalle + pikahaku-CTA */}
              <div className="rounded-xl bg-white/80 dark:bg-night-800/80 ring-1 ring-black/10 dark:ring-white/10 md:backdrop-blur-md p-6 sm:p-8 shadow-md">

                <div className="flex items-center gap-2">
                  <span
                    className={[pillBase, pillSelected, "cursor-default"].join(
                      " "
                    )}
                  >
                    <span className="px-0.5 font-medium">Miksi Buukkeri?</span>
                  </span>
                  <span className="text-xs text-black/60 dark:text-white/70">
                    Esiintyjän varaaminen helposti ja turvallisesti.
                  </span>
                </div>

                <h2 className="mt-3 font-customFont2 text-3xl sm:text-4xl leading-tight text-black/85 dark:text-primary">
                  Miksi Buukkeri?
                </h2>

                <p className="mt-2 font-customFont1 text-xs md:text-lg text-black/80 dark:text-primary/95">
                  Buukkeri on suomalainen palvelu, joka tekee esiintyjän
                  varaamisesta vaivatonta ja turvallista. Ei vaivaa tai
                  epävarmuutta – vain vapaat esiintyjät, selkeät hinnat ja sujuva
                  buukkaus.
                </p>

                {/* vasemman listan värit */}
                <ul className="mt-4 space-y-2.5 text-xs md:text-lg font-semibold text-black/80 dark:text-primary">
                  <Bullet>Kaikki esiintyjät yhdessä paikassa</Bullet>
                  <Bullet>Saatavuus ja hinnat reaaliajassa</Bullet>
                  <Bullet>Nopea tarjouspyyntö → tarjous → vahvistus</Bullet>
                  <Bullet>Arvostelut ja tähdet tukevat valintaa</Bullet>
                  <Bullet>Suodata päivän, sijainnin ja tyylin mukaan</Bullet>
                  <Bullet>Asiantunteva asiakaspalvelu</Bullet>
                </ul>

                <p className="mt-4 font-customFont1 text-base sm:text-lg text-black/80 dark:text-primary/95">
                  Suunnittele, buukkaa ja nauti juhlistasi – Buukkeri hoitaa
                  loput.
                </p>

                {/* ✅ Pikahaku-CTA asiakkaalle */}
                <div className="flex justify-start mt-6">
                  <ResponsiveButton
                    onClick={openBooking}
                    variant="primary"
                    className="w-full scale-[0.80] md:scale-100 font-semibold"
                    size="sm"
                    IconRight={ArrowRight}
                  >
                    Pikahaku
                  </ResponsiveButton>
                </div>
              </div>

              {/* Oikea paneeli: esiintyjille */}
              <div className="rounded-xl bg-night-800/100 dark:bg-night-800/80 ring-1 ring-white/10 md:backdrop-blur-md p-6 sm:p-8 shadow-md">

                <div className="flex items-center gap-2">
                  <span
                    className={[pillBase, pillSelected, "cursor-default"].join(
                      " "
                    )}
                  >
                    <span className="px-0.5 font-medium text-white">Esiintyjille</span>
                  </span>
                  <span className="text-xs text-white/70">
                    Profiili, kalenteri, tarjoukset – samassa paikassa.
                  </span>
                </div>

                <h3 className="mt-3 font-customFont2 text-3xl sm:text-4xl leading-tight text-primary">
                  Oletko esiintyjä?
                </h3>

                <p className="mt-2 font-customFont1 text-xs md:text-lg text-primary/95">
                  Buukkeri tekee buukkaamisesta vaivatonta myös artisteille. Luo
                  profiili, lisää keikkakalenterisi ja tavoita uudet asiakkaat
                  ilman ylimääräistä työtä.
                </p>

                {/* ✅ Liity esiintyjäksi -osan bulletit primaryksi myös light-modessa */}
                <ul className="mt-4 space-y-2.5 text-xs md:text-lg font-semibold text-primary">
                  <Bullet>Profiilisivu omalla osoitteella</Bullet>
                  <Bullet>
                    Hallintapaneeli: musiikkityylit, soittimet, lisäpalvelut,
                    some, YouTube/SoundCloud
                  </Bullet>
                  <Bullet>Hinnat ja keikkailualueet maakunnittain</Bullet>
                  <Bullet>Reaaliaikainen kalenteri + Google-synkka</Bullet>
                  <Bullet>Automaattinen matkakululaskenta (0,59 €/km)</Bullet>
                  <Bullet>Automaattiset palautepyynnöt</Bullet>
                  <Bullet>Kuukauden esiintyjä -nosto</Bullet>
                  <Bullet>Tarjous asiakkaalle könttäsummana</Bullet>
                  <Bullet>
                    Usean esiintymiskortin hallinta yhdellä profiililla
                  </Bullet>
                  <Bullet>Puskuriasetus: minimivaroaika</Bullet>
                </ul>

                <p className="mt-4 font-customFont1 text-base sm:text-lg text-primary/95">
                  Liity Buukkeriin ja kasvata keikkamäärääsi vaivatta.
                </p>

                <div className="mt-6">
                  <ResponsiveButton
                    variant="secondary"
                    className="w-full scale-[0.80] md:scale-100 font-semibold text-white"
                    onClick={() => (window.location.href = learnMoreHref)}
                  >
                    Liity esiintyjäksi
                  </ResponsiveButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

WhyBuukkeriSection.propTypes = {
  className: PropTypes.string,
  learnMoreHref: PropTypes.string,
};
